import { forwardRef } from 'react';
import { TextField } from '@mui/material';
import { colorTokens, radiusTokens, typographyTokens } from '../../app/designTokens';

const { semantic } = colorTokens;

/**
 * Shared Form / TextInput (Figma Base).
 * @param {'Normal'|'Compact'|'normal'|'compact'} [density]
 * @param {boolean} [hideLabel] - Keep aria/placeholder without floating label (filters)
 * @param {boolean} [error]
 * @param {boolean} [readOnly]
 * @param {string} [helperText]
 */
export const TextInput = forwardRef(function TextInput(
  {
    density = 'Normal',
    error = false,
    disabled = false,
    readOnly = false,
    label,
    helperText,
    hideLabel = false,
    placeholder,
    InputProps,
    sx,
    ...rest
  },
  ref,
) {
  const isCompact = density === 'Compact' || density === 'compact';
  const showLabel = Boolean(label) && !hideLabel;
  const resolvedPlaceholder = placeholder ?? (hideLabel && label ? label : undefined);

  return (
    <TextField
      ref={ref}
      label={showLabel ? label : undefined}
      placeholder={resolvedPlaceholder}
      error={error}
      disabled={disabled}
      helperText={helperText}
      size={isCompact ? 'small' : 'medium'}
      fullWidth
      InputProps={{
        readOnly,
        ...InputProps,
      }}
      inputProps={{
        'aria-label': !showLabel && label ? label : undefined,
        ...(rest.inputProps || {}),
      }}
      InputLabelProps={showLabel ? { shrink: true } : undefined}
      sx={{
        '& .MuiInputLabel-root': {
          fontFamily: typographyTokens.fontFamily,
          fontWeight: typographyTokens.fontWeightSemibold,
          fontSize: 13,
          color: semantic.text.secondary,
          '&.Mui-focused': {
            color: semantic.text.secondary,
          },
          '&.Mui-error': {
            color: semantic.error,
          },
        },
        '& .MuiOutlinedInput-root': {
          minHeight: isCompact ? 40 : 44,
          borderRadius: `${radiusTokens.search}px`,
          bgcolor: semantic.bg.input,
          color: semantic.text.primary,
          fontFamily: typographyTokens.fontFamily,
          fontSize: 13,
          transition: 'border-color 120ms ease, background-color 120ms ease',
          '& .MuiOutlinedInput-input': {
            py: isCompact ? '8.5px' : '10px',
            '&::placeholder': {
              color: semantic.text.muted,
              opacity: 1,
            },
          },
          '& fieldset': {
            borderColor: error ? semantic.error : semantic.border.default,
            borderWidth: 1,
          },
          '&:hover fieldset': {
            borderColor: error ? semantic.error : semantic.border.default,
          },
          '&.Mui-focused': {
            bgcolor: semantic.bg.input,
            '& fieldset': {
              borderColor: error ? semantic.error : semantic.text.secondary,
              borderWidth: 1,
            },
          },
          '&.Mui-disabled': {
            bgcolor: semantic.action.secondary.bg,
            '& fieldset': {
              borderColor: semantic.border.subtle,
            },
            '& .MuiOutlinedInput-input': {
              WebkitTextFillColor: semantic.text.disabled,
              color: semantic.text.disabled,
            },
          },
          ...(readOnly
            ? {
                bgcolor: semantic.bg.surface,
                '& fieldset': {
                  borderColor: semantic.border.subtle,
                },
              }
            : null),
        },
        '& .MuiFormHelperText-root': {
          fontFamily: typographyTokens.fontFamily,
          fontSize: 12,
          letterSpacing: '0.24px',
          color: error ? semantic.error : semantic.text.muted,
          mx: 0,
          mt: 0.75,
        },
        ...sx,
      }}
      {...rest}
    />
  );
});

export default TextInput;
